/** Remove acentos e normaliza para buscas/slugs. */
export function stripAccents(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export function normalizeSearch(s: string): string {
  return stripAccents(s).toLowerCase().trim().replace(/\s+/g, ' ');
}

/** "Dezza Fit Wear!" -> "dezza-fit-wear" */
export function slugify(s: string, maxLength = 48): string {
  return stripAccents(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, maxLength)
    .replace(/-+$/g, '');
}

/** "Deslane Souza" -> "DS"; "Dezzafitwear" -> "DE"; "[Promo] Top" -> "PT" (ignora símbolos). */
export function initials(name: string): string {
  const parts = name
    .trim()
    .split(/\s+/)
    .map((p) => p.replace(/[^\p{L}\p{N}]/gu, ''))
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

export function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

const DISPLAY_MINOR_WORDS = new Set(['a', 'à', 'ao', 'aos', 'as', 'com', 'da', 'das', 'de', 'do', 'dos', 'e', 'em', 'na', 'nas', 'no', 'nos', 'o', 'os', 'ou', 'para', 'por', 'sem']);
const DISPLAY_KEEP_UPPER = new Set(['pp', 'gg', 'xg', 'xgg', 'eg', 'egg', 'uv', 'dz', 'g1', 'g2', 'g3', 'g4']);

/**
 * Nome para exibição: textos todos em caixa alta viram "Título" ("CONJUNTOS DE SHORTS" vira
 * "Conjuntos de Shorts"). Nomes que já têm minúsculas ficam como estão. Só exibição: o dado não muda.
 * Usado no PDV, no catálogo e nas listas do painel para categorias cadastradas em caixa alta.
 */
export function displayCase(name: string): string {
  const letters = name.replace(/[^\p{L}]/gu, '');
  if (letters.length < 2 || letters !== letters.toLocaleUpperCase('pt-BR')) return name;
  let first = true;
  return name
    .toLocaleLowerCase('pt-BR')
    .split(/([\s\-/+]+)/)
    .map((w) => {
      if (!w || /^[\s\-/+]+$/.test(w)) return w;
      const isFirst = first;
      first = false;
      if (!isFirst && DISPLAY_MINOR_WORDS.has(w)) return w;
      if (DISPLAY_KEEP_UPPER.has(w) || /\d/.test(w) || w.length === 1) return w.toLocaleUpperCase('pt-BR');
      return w.charAt(0).toLocaleUpperCase('pt-BR') + w.slice(1);
    })
    .join('');
}

/** Palavras reservadas que não podem ser usadas como slug de loja. */
export const RESERVED_SLUGS = new Set([
  'www', 'app', 'api', 'admin', 'painel', 'loja', 'lojas', 'static', 'assets', 'cdn', 'mail', 'email',
  'suporte', 'ajuda', 'help', 'status', 'blog', 'docs', 'dev', 'staging', 'test', 'teste', 'dezza',
  'login', 'entrar', 'cadastro', 'precos', 'planos', 'checkout', 'pedido', 'pedidos', 'r', 'feeds',
  'uploads', 'webhooks', 'health', 'sitemap', 'robots', 'favicon',
]);

export function isValidSlug(slug: string): boolean {
  return /^[a-z0-9](?:[a-z0-9-]{1,46}[a-z0-9])$/.test(slug) && !RESERVED_SLUGS.has(slug);
}
